import { useNavigate } from '@solidjs/router'
import {
  IconArrowDown,
  IconArrowUp,
  IconBuilding,
  IconBuildingWarehouse,
  IconClipboardList,
  IconCornerDownLeft,
  IconSearch,
  IconSparkles,
} from '@tabler/icons-solidjs'
import { cn, Kbd } from '@veem/ui'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  type JSX,
  Match,
  Show,
  Switch,
} from 'solid-js'
import { productById } from '../data/catalog.ts'
import { CUSTOMER_KIND_LABELS, contactsOf } from '../data/customers.ts'
import type { ProtoOrder } from '../data/orders.ts'
import { personById } from '../data/people.ts'
import type { ProtoMaterial } from '../data/warehouse.ts'
import { compare, sub } from '../lib/decimal.ts'
import { formatMoney, formatQuantity } from '../lib/format.ts'
import { allNavItems, BASE } from '../lib/nav.tsx'
import { can } from '../lib/permissions.ts'
import { proto } from '../state/store.ts'
import { StageBadge } from './primitives.tsx'

type Scope = 'all' | 'orders' | 'companies' | 'materials'

type Result =
  | { kind: 'order'; id: string; title: string; meta: string; href: string; order: ProtoOrder }
  | { kind: 'company'; id: string; title: string; meta: string; href: string }
  | {
      kind: 'material'
      id: string
      title: string
      meta: string
      href: string
      material: ProtoMaterial
    }
  | { kind: 'page'; id: string; title: string; meta: string; href: string; icon: () => JSX.Element }

const SCOPES: { key: Scope; label: string }[] = [
  { key: 'all', label: 'Усе' },
  { key: 'orders', label: 'Замовлення' },
  { key: 'companies', label: 'Клієнти' },
  { key: 'materials', label: 'Матеріали' },
]

const normalize = (value: string) => value.toLocaleLowerCase('uk-UA')

/**
 * Global search across records, as BRD §4.10 asks: orders by name, client, ЗМ number or product;
 * companies; materials. No open/close animation: it is opened from the keyboard many times a day.
 */
export const CommandSearch = (props: { open: boolean; onClose: () => void; onAsk: () => void }) => {
  const navigate = useNavigate()
  const [query, setQuery] = createSignal('')
  const [scope, setScope] = createSignal<Scope>('all')
  const [cursor, setCursor] = createSignal(0)
  let dialog!: HTMLDialogElement
  let input!: HTMLInputElement
  let list!: HTMLDivElement

  createEffect(() => {
    if (props.open && !dialog.open) {
      dialog.showModal()
      setQuery('')
      setCursor(0)
      queueMicrotask(() => input.focus())
    } else if (!props.open && dialog.open) dialog.close()
  })

  const results = createMemo<Result[]>(() => {
    const role = proto.role()
    const term = normalize(query().trim())
    const matches = (...values: (string | null | undefined)[]) =>
      !term || values.some((value) => value && normalize(value).includes(term))
    const out: Result[] = []
    if ((scope() === 'all' || scope() === 'orders') && can(role, 'deals', 'read')) {
      for (const order of proto.state.orders) {
        const products = order.lines.map((line) => productById(line.productId).name)
        if (!matches(order.deal.name, order.deal.clientName, order.production?.number, ...products))
          continue
        out.push({
          kind: 'order',
          id: order.deal.id,
          title: order.deal.name,
          meta: [order.production?.number, order.deal.clientName].filter(Boolean).join(' · '),
          href: `${BASE}/orders/${order.deal.id}`,
          order,
        })
      }
    }
    if ((scope() === 'all' || scope() === 'companies') && can(role, 'deals', 'read')) {
      for (const customer of proto.state.customers) {
        const people = contactsOf(customer.id, proto.state.links, proto.state.persons)
        const person = term
          ? people.find((candidate) =>
              matches(candidate.fullName, candidate.phone, candidate.email),
            )
          : undefined
        if (!person && !matches(customer.name, customer.edrpou, customer.city, customer.phone))
          continue
        out.push({
          kind: 'company',
          id: customer.id,
          title: customer.name,
          meta: person
            ? `${person.fullName}, ${person.role.toLocaleLowerCase('uk-UA')}`
            : `${CUSTOMER_KIND_LABELS[customer.kind]} · ${customer.city}`,
          href: `${BASE}/customers/${customer.id}`,
        })
      }
    }
    if ((scope() === 'all' || scope() === 'materials') && can(role, 'warehouse', 'read')) {
      for (const material of proto.state.materials) {
        if (!matches(material.name, material.grade, material.category)) continue
        out.push({
          kind: 'material',
          id: material.id,
          title: material.name,
          meta: `${material.grade ?? ''} · ${material.warehouse}`,
          href: `${BASE}/warehouse?material=${material.id}`,
          material,
        })
      }
    }
    if (scope() === 'all') {
      for (const item of allNavItems()) {
        if (!can(role, item.resource, item.action ?? 'read') || !matches(item.label)) continue
        out.push({
          kind: 'page',
          id: item.href,
          title: item.label,
          meta: 'Розділ',
          href: item.href,
          icon: item.icon,
        })
      }
    }
    return scope() === 'all' && !term ? out.slice(0, 12) : out
  })

  const active = () => results()[Math.min(cursor(), results().length - 1)]

  const go = (result: Result | undefined) => {
    if (!result) return
    props.onClose()
    navigate(result.href)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const length = results().length
      if (!length) return
      setCursor((value) => (value + (event.key === 'ArrowDown' ? 1 : length - 1)) % length)
      list.querySelector(`[data-index="${cursor()}"]`)?.scrollIntoView({ block: 'nearest' })
    } else if (event.key === 'Enter') {
      event.preventDefault()
      go(active())
    } else if (event.key === 'Tab') {
      event.preventDefault()
      const index = SCOPES.findIndex((item) => item.key === scope())
      const next = SCOPES[(index + (event.shiftKey ? SCOPES.length - 1 : 1)) % SCOPES.length]
      if (next) setScope(next.key)
      setCursor(0)
    }
  }

  const iconFor = (result: Result) => (
    <Switch>
      <Match when={result.kind === 'order'}>
        <IconClipboardList stroke-width={1.5} />
      </Match>
      <Match when={result.kind === 'company'}>
        <IconBuilding stroke-width={1.5} />
      </Match>
      <Match when={result.kind === 'material'}>
        <IconBuildingWarehouse stroke-width={1.5} />
      </Match>
      <Match when={result.kind === 'page' && result}>
        {(page) => (page() as Extract<Result, { kind: 'page' }>).icon()}
      </Match>
    </Switch>
  )

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: a backdrop click mirrors the dialog's native Escape key
    <dialog
      ref={dialog}
      aria-label="Пошук"
      onClose={() => props.onClose()}
      onClick={(event) => event.target === dialog && props.onClose()}
      class="fixed inset-0 m-auto mt-[12vh] h-fit max-h-[min(36rem,76dvh)] w-[min(56rem,calc(100vw-1.5rem))] overflow-hidden rounded-panel border border-border bg-surface p-0 text-text shadow-overlay backdrop:bg-ink/30"
    >
      <div class="flex items-center gap-3 border-b border-border px-4">
        <IconSearch
          class="size-4 shrink-0 text-text-subtle"
          stroke-width={1.5}
          aria-hidden="true"
        />
        <input
          ref={input}
          value={query()}
          onInput={(event) => {
            setQuery(event.currentTarget.value)
            setCursor(0)
          }}
          onKeyDown={onKeyDown}
          type="search"
          name="global-search"
          autocomplete="off"
          spellcheck={false}
          role="combobox"
          aria-expanded="true"
          aria-controls="proto-search-results"
          aria-activedescendant={active() ? `proto-result-${active()?.id}` : undefined}
          placeholder="Пошук замовлень, клієнтів, людей, матеріалів, ЗМ-номерів…"
          class="h-12 min-w-0 flex-1 bg-transparent text-ui text-text placeholder:text-text-subtle focus:outline-none max-md:text-lead"
        />
        <button
          type="button"
          onClick={() => props.onAsk()}
          class="hidden items-center gap-1.5 rounded-control px-2 py-1 text-body text-text-muted hover:bg-surface-sunken sm:inline-flex"
        >
          <IconSparkles class="size-4" stroke-width={1.5} aria-hidden="true" />
          Запитати помічника
        </button>
      </div>
      <div role="tablist" aria-label="Де шукати" class="flex gap-4 border-b border-border px-4">
        <For each={SCOPES}>
          {(item) => (
            <button
              type="button"
              role="tab"
              aria-selected={scope() === item.key}
              onClick={() => {
                setScope(item.key)
                setCursor(0)
                input.focus()
              }}
              class={cn(
                '-mb-px border-b-2 py-2.5 text-body font-medium',
                scope() === item.key
                  ? 'border-accent text-text'
                  : 'border-transparent text-text-subtle hover:text-text',
              )}
            >
              {item.label}
            </button>
          )}
        </For>
      </div>
      <div class="grid min-h-0 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div
          ref={list}
          id="proto-search-results"
          role="listbox"
          aria-label="Результати"
          class="max-h-[min(24rem,50dvh)] overflow-y-auto overscroll-contain p-1.5 md:border-r md:border-border"
        >
          <Show
            when={results().length > 0}
            fallback={
              <p role="status" class="px-3 py-8 text-center text-body text-text-subtle">
                Нічого не знайдено. Спробуйте номер ЗМ, назву клієнта або ім’я людини.
              </p>
            }
          >
            <For each={results()}>
              {(result, index) => (
                <button
                  type="button"
                  id={`proto-result-${result.id}`}
                  data-index={index()}
                  role="option"
                  aria-selected={index() === cursor()}
                  onMouseMove={() => setCursor(index())}
                  onClick={() => go(result)}
                  class={cn(
                    'flex w-full items-center gap-3 rounded-control px-2.5 py-2 text-left max-md:min-h-(--size-touch)',
                    '[&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-text-subtle',
                    index() === cursor() && 'bg-surface-sunken',
                  )}
                >
                  {iconFor(result)}
                  <span class="grid min-w-0 flex-1">
                    <span class="truncate text-body font-medium text-text">{result.title}</span>
                    <span class="truncate text-caption text-text-subtle">{result.meta}</span>
                  </span>
                </button>
              )}
            </For>
          </Show>
        </div>
        <div class="hidden max-h-[min(24rem,50dvh)] overflow-y-auto p-4 md:block">
          <Show when={active()} fallback={<p class="text-body text-text-subtle">Оберіть запис</p>}>
            {(result) => <Preview result={result()} />}
          </Show>
        </div>
      </div>
      <div class="flex items-center gap-3 border-t border-border bg-surface-muted px-4 py-2 text-caption text-text-subtle">
        <span class="inline-flex items-center gap-1">
          <Kbd>
            <IconArrowUp class="size-3" aria-hidden="true" />
          </Kbd>
          <Kbd>
            <IconArrowDown class="size-3" aria-hidden="true" />
          </Kbd>
          вибрати
        </span>
        <span class="inline-flex items-center gap-1">
          <Kbd>
            <IconCornerDownLeft class="size-3" aria-hidden="true" />
          </Kbd>
          відкрити
        </span>
        <span class="inline-flex items-center gap-1">
          <Kbd>Tab</Kbd>
          розділ
        </span>
        <span class="ml-auto inline-flex items-center gap-1">
          <Kbd>Esc</Kbd>
          закрити
        </span>
      </div>
    </dialog>
  )
}

const Fact = (props: { label: string; children: JSX.Element }) => (
  <div class="grid gap-0.5">
    <dt class="text-caption text-text-subtle">{props.label}</dt>
    <dd class="text-body text-text">{props.children}</dd>
  </div>
)

const Preview = (props: { result: Result }) => (
  <Switch>
    <Match
      when={props.result.kind === 'order' && (props.result as Extract<Result, { kind: 'order' }>)}
    >
      {(result) => {
        const order = () => result().order
        return (
          <div class="grid gap-4">
            <div class="grid gap-1">
              <p class="text-caption text-text-subtle">{order().deal.clientName}</p>
              <p class="text-lead font-semibold tracking-tight">{order().deal.name}</p>
              <div class="mt-1 flex flex-wrap items-center gap-2">
                <StageBadge stageKey={order().deal.stage} />
                <Show when={order().production}>
                  {(production) => (
                    <span class="font-mono text-caption text-text-muted">
                      {production().number}
                    </span>
                  )}
                </Show>
              </div>
            </div>
            <dl class="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border pt-4">
              <Fact label="Сума">
                <span class="font-mono tabular-nums">
                  {formatMoney(order().deal.amount, order().deal.currencyCode)}
                </span>
              </Fact>
              <Fact label="Менеджер">{personById(order().deal.managerId).name}</Fact>
              <Fact label="Наша компанія">{order().deal.sellerEntityName ?? '—'}</Fact>
              <Fact label="Позицій">{order().lines.length}</Fact>
            </dl>
          </div>
        )
      }}
    </Match>
    <Match
      when={
        props.result.kind === 'material' && (props.result as Extract<Result, { kind: 'material' }>)
      }
    >
      {(result) => {
        const material = () => result().material
        const available = () => sub(material().physical, material().reserved)
        return (
          <div class="grid gap-4">
            <div class="grid gap-1">
              <p class="text-caption text-text-subtle">{material().category}</p>
              <p class="text-lead font-semibold tracking-tight">{material().name}</p>
              <p class="text-body text-text-muted">{material().grade}</p>
            </div>
            <dl class="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border pt-4">
              <Fact label="Фізично">
                <span class="font-mono tabular-nums">
                  {formatQuantity(material().physical, material().unit)}
                </span>
              </Fact>
              <Fact label="Доступно">
                <span
                  class={cn(
                    'font-mono tabular-nums',
                    compare(available(), material().critical) <= 0 && 'text-status-critical',
                  )}
                >
                  {formatQuantity(available(), material().unit)}
                </span>
              </Fact>
              <Fact label="Склад">{material().warehouse}</Fact>
              <Fact label="Комірка">{material().location}</Fact>
            </dl>
          </div>
        )
      }}
    </Match>
    <Match when={props.result}>
      <div class="grid gap-1">
        <p class="text-lead font-semibold tracking-tight">{props.result.title}</p>
        <p class="text-body text-text-subtle">{props.result.meta}</p>
      </div>
    </Match>
  </Switch>
)
